/**
 * FL-246: スケジュール管理フィルターパネル・コンポーネント
 *
 * 管理者がドライバー・ステータス・日付でスケジュールをフィルタリングするUI
 * フィルター条件をセッションストレージに保存し、ページ再読込後も復元
 *
 * TDD Red-Green-Refactorサイクルに従った実装
 */

import React, { useState, useEffect } from 'react';

/**
 * フィルター条件の型定義
 */
export interface ScheduleFilter {
  /** 選択されたドライバーIDリスト */
  driverIds: string[];
  /** 選択されたステータスリスト */
  statuses: ('registered' | 'confirmed' | 'cancelled')[];
  /** 日付範囲 */
  dateRange: {
    start: string; // YYYY-MM-DD
    end: string; // YYYY-MM-DD
  };
}

interface ScheduleFilterPanelProps {
  /** 利用可能なドライバーリスト */
  drivers: Array<{ id: string; name: string }>;
  /** フィルター変更時のコールバック */
  onFilterChange: (filter: ScheduleFilter) => void;
  /** 初期フィルター値（オプション） */
  initialFilter?: ScheduleFilter;
}

/**
 * デフォルトフィルター値を生成
 */
const getDefaultFilter = (): ScheduleFilter => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    driverIds: [],
    statuses: ['registered', 'confirmed', 'cancelled'],
    dateRange: {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0],
    },
  };
};

/**
 * セッションストレージからフィルターを復元
 */
const loadFilterFromSession = (): ScheduleFilter | null => {
  try {
    const stored = sessionStorage.getItem('scheduleFilter');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load filter from sessionStorage:', error);
    return null;
  }
};

/**
 * セッションストレージにフィルターを保存
 */
const saveFilterToSession = (filter: ScheduleFilter): void => {
  try {
    sessionStorage.setItem('scheduleFilter', JSON.stringify(filter));
  } catch (error) {
    console.warn('Failed to save filter to sessionStorage:', error);
  }
};

/**
 * ScheduleFilterPanel コンポーネント
 *
 * @param drivers - 利用可能なドライバーリスト
 * @param onFilterChange - フィルター変更時のコールバック
 * @param initialFilter - 初期フィルター値
 * @returns JSX要素
 */
export const ScheduleFilterPanel: React.FC<ScheduleFilterPanelProps> = ({
  drivers,
  onFilterChange,
  initialFilter,
}) => {
  // フィルター状態の初期化
  const [filter, setFilter] = useState<ScheduleFilter>(() => {
    // 優先順位: 初期フィルター > セッションストレージ > デフォルト
    return initialFilter || loadFilterFromSession() || getDefaultFilter();
  });

  const [dateError, setDateError] = useState<string>('');

  // フィルター変更時、親コンポーネントに通知
  useEffect(() => {
    onFilterChange(filter);
  }, [filter, onFilterChange]);

  // フィルター変更時、セッションストレージに保存
  useEffect(() => {
    saveFilterToSession(filter);
  }, [filter]);

  /**
   * ドライバー選択変更ハンドラー
   */
  const handleDriverToggle = (driverId: string) => {
    setFilter(prev => {
      const newDriverIds = prev.driverIds.includes(driverId)
        ? prev.driverIds.filter(id => id !== driverId)
        : [...prev.driverIds, driverId];

      return { ...prev, driverIds: newDriverIds };
    });
  };

  /**
   * 全ドライバー選択
   */
  const handleSelectAllDrivers = () => {
    setFilter(prev => ({
      ...prev,
      driverIds: drivers.map(d => d.id),
    }));
  };

  /**
   * ドライバー選択解除
   */
  const handleDeselectAllDrivers = () => {
    setFilter(prev => ({
      ...prev,
      driverIds: [],
    }));
  };

  /**
   * ステータス選択変更ハンドラー
   */
  const handleStatusToggle = (status: 'registered' | 'confirmed' | 'cancelled') => {
    setFilter(prev => {
      const newStatuses = prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status];

      return { ...prev, statuses: newStatuses };
    });
  };

  /**
   * 開始日変更ハンドラー
   */
  const handleStartDateChange = (date: string) => {
    setDateError('');

    // 日付の妥当性チェック
    if (date > filter.dateRange.end) {
      setDateError('開始日は終了日より前である必要があります');
      return;
    }

    setFilter(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, start: date },
    }));
  };

  /**
   * 終了日変更ハンドラー
   */
  const handleEndDateChange = (date: string) => {
    setDateError('');

    // 日付の妥当性チェック
    if (filter.dateRange.start > date) {
      setDateError('終了日は開始日より後である必要があります');
      return;
    }

    setFilter(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, end: date },
    }));
  };

  /**
   * フィルタークリア
   */
  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter();
    setFilter(defaultFilter);
    setDateError('');
    sessionStorage.removeItem('scheduleFilter');
  };

  return (
    <div
      data-testid='schedule-filter-panel'
      className='space-y-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'
    >
      {/* タイトル */}
      <div>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
          スケジュールフィルター
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          表示するスケジュールの条件を設定します
        </p>
      </div>

      {/* ドライバーフィルター */}
      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
          ドライバー
        </label>
        <div className='space-y-2'>
          {/* 全選択・選択解除ボタン */}
          <div className='flex gap-2 mb-2'>
            <button
              type='button'
              onClick={handleSelectAllDrivers}
              className='text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              data-testid='select-all-drivers'
            >
              全て選択
            </button>
            <button
              type='button'
              onClick={handleDeselectAllDrivers}
              className='text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              data-testid='deselect-all-drivers'
            >
              選択解除
            </button>
          </div>

          {/* ドライバー一覧 */}
          <div className='grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto bg-gray-50 dark:bg-gray-700 p-2 rounded'>
            {drivers.map(driver => (
              <label key={driver.id} className='flex items-center cursor-pointer'>
                <input
                  type='checkbox'
                  checked={filter.driverIds.includes(driver.id)}
                  onChange={() => handleDriverToggle(driver.id)}
                  className='h-4 w-4 rounded border-gray-300 text-blue-600'
                  data-testid={`driver-checkbox-${driver.id}`}
                />
                <span className='ml-2 text-sm text-gray-700 dark:text-gray-300'>{driver.name}</span>
              </label>
            ))}
          </div>

          {/* 選択状態表示 */}
          <p className='text-xs text-gray-600 dark:text-gray-400'>
            {filter.driverIds.length}人選択
          </p>
        </div>
      </div>

      {/* ステータスフィルター */}
      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
          ステータス
        </label>
        <div className='space-y-2'>
          {[
            { value: 'registered' as const, label: '登録' },
            { value: 'confirmed' as const, label: '確定' },
            { value: 'cancelled' as const, label: 'キャンセル' },
          ].map(status => (
            <label key={status.value} className='flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={filter.statuses.includes(status.value)}
                onChange={() => handleStatusToggle(status.value)}
                className='h-4 w-4 rounded border-gray-300 text-blue-600'
                data-testid={`status-checkbox-${status.value}`}
              />
              <span className='ml-2 text-sm text-gray-700 dark:text-gray-300'>{status.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* 日付範囲フィルター */}
      <div>
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
          日付範囲
        </label>
        <div className='space-y-2'>
          <div className='grid grid-cols-2 gap-2'>
            <div>
              <label htmlFor='start-date' className='text-xs text-gray-600 dark:text-gray-400'>
                開始日
              </label>
              <input
                id='start-date'
                type='date'
                value={filter.dateRange.start}
                onChange={e => handleStartDateChange(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm'
                data-testid='start-date-input'
              />
            </div>
            <div>
              <label htmlFor='end-date' className='text-xs text-gray-600 dark:text-gray-400'>
                終了日
              </label>
              <input
                id='end-date'
                type='date'
                value={filter.dateRange.end}
                onChange={e => handleEndDateChange(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm'
                data-testid='end-date-input'
              />
            </div>
          </div>

          {/* エラーメッセージ */}
          {dateError && <p className='text-sm text-red-600 dark:text-red-400'>{dateError}</p>}
        </div>
      </div>

      {/* クリアボタン */}
      <div className='flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700'>
        <button
          type='button'
          onClick={handleClearFilter}
          className='px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          data-testid='clear-filter-button'
        >
          クリア
        </button>
      </div>
    </div>
  );
};

export default ScheduleFilterPanel;
